import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { profileSystems } from '../data/mockData';
import ColorPicker from './ColorPicker';
import ConfiguratorAdditionalItems from './ConfiguratorAdditionalItems';
import ProfileSystemPicker from './ProfileSystemPicker';
import { useTranslation } from '../context/TranslationContext';
import {
  KERALIT_MIN_DIMENSION_MM,
  KERALIT_MAX_DIMENSION_MM,
} from '../lib/pdfPricing';

const KERALIT_MIN_DIMENSION = KERALIT_MIN_DIMENSION_MM;
const KERALIT_MAX_DIMENSION = KERALIT_MAX_DIMENSION_MM;

const KeralitOptionsPanel = ({
  config,
  onConfigChange,
  additionalItems,
  onAdditionalItemsChange,
}) => {
  const { t } = useTranslation();
  const handleWidthChange = (e) => {
    const value = parseInt(e.target.value) || 0;
    const clamped = Math.min(KERALIT_MAX_DIMENSION, Math.max(KERALIT_MIN_DIMENSION, value));
    onConfigChange('width', clamped);
  };

  const handleHeightChange = (e) => {
    const value = parseInt(e.target.value) || 0;
    const clamped = Math.min(KERALIT_MAX_DIMENSION, Math.max(KERALIT_MIN_DIMENSION, value));
    onConfigChange('height', clamped);
  };

  return (
    <div className="space-y-4">
      <ProfileSystemPicker
        profileSystems={profileSystems}
        config={config}
        onConfigChange={onConfigChange}
        translateName={(id, name) => ({ schuco: t('options.schuco'), synego: t('options.synego') }[id] || name)}
        translateDescription={(id, description) => ({ schuco: t('options.schucoDesc'), synego: t('options.synegoDesc') }[id] || description)}
      />

      {/* Dimensions */}
      <Card>
        <CardHeader>
          <CardTitle>{t('options.dimensions')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="width">{t('options.width')}</Label>
            <Input
              id="width"
              type="number"
              min={KERALIT_MIN_DIMENSION}
              max={KERALIT_MAX_DIMENSION}
              value={config.width || 1500}
              onChange={handleWidthChange}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="height">{t('options.height')}</Label>
            <Input
              id="height"
              type="number"
              min={KERALIT_MIN_DIMENSION}
              max={KERALIT_MAX_DIMENSION}
              value={config.height || 1500}
              onChange={handleHeightChange}
              className="mt-1"
            />
          </div>
        </CardContent>
      </Card>

      {/* Colors */}
      <Card>
        <CardHeader>
          <CardTitle>{t('options.colors')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>{t('options.interiorColor')}</Label>
            <CardDescription className="mb-2">Search by RAL code or color name</CardDescription>
            <ColorPicker
              selectedColor={config.interiorColor || 'white'}
              onColorChange={(colorId) => onConfigChange('interiorColor', colorId)}
            />
          </div>
          <div>
            <Label>{t('options.exteriorColor')}</Label>
            <CardDescription className="mb-2">Search by RAL code or color name</CardDescription>
            <ColorPicker
              selectedColor={config.exteriorColor || 'anthracite'}
              onColorChange={(colorId) => onConfigChange('exteriorColor', colorId)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Panel Lines */}
      <Card>
        <CardHeader>
          <CardTitle>{t('options.lineType')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div>
            <Label>{t('options.lineType')}</Label>
            <Select
              value={config.lineType || 'horizontal'}
              onValueChange={(value) => onConfigChange('lineType', value)}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select line type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="horizontal">Horizontal Lines</SelectItem>
                <SelectItem value="vertical">Vertical Lines</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      {onAdditionalItemsChange ? (
        <ConfiguratorAdditionalItems
          items={additionalItems}
          onItemsChange={onAdditionalItemsChange}
        />
      ) : null}
    </div>
  );
};

export default KeralitOptionsPanel;

